import React, { useEffect, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { CheckboxList, ListItem, Select, Ellipsis } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { useQuery } from '@apollo/client'
import { useZionFormContext, useWatch } from '@fs/zion-form'

import { trackEvent } from '@fs/zion-analytics'
import { useGroupSearch, useLocalDataCache } from 'hooks'
import { css } from '@emotion/core'
import { getLifeEvents } from '../queries'
import transl from '../FormInputs.transl'
import { ExpanderList } from '../../ExpanderList/ExpanderList'

export default function LifeEvents({
  whatFacets,
  showCheckBoxes = true,
  returnLifeEventObj = false,
  disabled = false,
}) {
  const { getValues, setValue } = useZionFormContext()
  const [selectedLifeEvent, setSelectedLifeEvent] = useState(null)
  const [values] = useState(getValues())
  const { groupSearchQueryParams } = useGroupSearch()
  const { lifeEvents, setLifeEvents } = useLocalDataCache()
  const primaryRecordTypesList = selectedLifeEvent?.primaryRecordTypes || []
  const relatedRecordTypesList = selectedLifeEvent?.relatedRecordTypes || []
  // For landing page to show life events
  const { data, loading } = useQuery(getLifeEvents, { errorPolicy: 'all' })
  const [previousPlace, setPreviousPlace] = useState()
  const watchedLifeEvent = useWatch({ name: 'lifeEvent' })

  const formattedLifeEvents = useMemo(
    () =>
      data?.lifeEvents?.map(({ key, primaryText }) => ({
        id: key,
        name: primaryText,
      })) || [],
    [data]
  )

  const lifeEventList = useMemo(() => {
    if (watchedLifeEvent === null) return []
    if (selectedLifeEvent?.primaryRecordTypes?.length > 0) return selectedLifeEvent.primaryRecordTypes
    if (selectedLifeEvent?.relatedRecordTypes?.length > 0) return selectedLifeEvent.relatedRecordTypes
    return []
  }, [selectedLifeEvent, watchedLifeEvent])

  useEffect(() => {
    if (watchedLifeEvent === 'form-reset') {
      setValue('lifeEvent', null)
      setValue('lifeEventRecordTypes', null)
      setSelectedLifeEvent(null)
    }
  }, [watchedLifeEvent, setValue])

  useEffect(() => {
    if (whatFacets?.length > 0) {
      const lifeEvent = groupSearchQueryParams?.lifeEvent
      const place = groupSearchQueryParams?.place
      if (lifeEvent == null || whatFacets.findIndex((event) => event.id === lifeEvent) !== -1) {
        setLifeEvents(whatFacets)
      } else if (previousPlace === place) {
        // if the life event in the url isn't already an option, add it as an option
        const selectedEvent = data?.lifeEvents?.find((event) => event.key === lifeEvent)
        if (selectedEvent) {
          setLifeEvents([...whatFacets, { id: selectedEvent.key, name: selectedEvent.primaryText }])
          setPreviousPlace(place)
        }
      }
    } else if (!loading && lifeEvents?.length === 0) {
      const eventsToSet = formattedLifeEvents || []
      if (eventsToSet.length !== 0 || lifeEvents.length !== 0) setLifeEvents(eventsToSet)
    } else if (formattedLifeEvents?.length !== lifeEvents?.length) setLifeEvents(formattedLifeEvents)
  }, [
    data,
    groupSearchQueryParams,
    lifeEvents,
    loading,
    previousPlace,
    setLifeEvents,
    whatFacets,
    formattedLifeEvents,
    watchedLifeEvent,
  ])

  // Set selected life event when loading from URL
  useEffect(() => {
    const selectedTopLevelLifeEvent = lifeEvents?.find((lifeEvent) =>
      lifeEvent ? lifeEvent?.id === groupSearchQueryParams?.lifeEvent : false
    )

    setSelectedLifeEvent(groupSearchQueryParams?.lifeEvent ? selectedTopLevelLifeEvent : null)
  }, [data, groupSearchQueryParams, lifeEvents])

  // Clear selected life event when form is reset
  useEffect(() => {
    if (!getValues().lifeEvent) setSelectedLifeEvent(null)
  }, [getValues])

  function selectHandler(event) {
    if (!event.target.value) {
      /* "All" is selected */
      setValue('lifeEvent', null)
      setValue('lifeEventRecordTypes', null)
      setSelectedLifeEvent(null)
    } else {
      const val = event.target.value
      const parsedVal = JSON.parse(val)
      setValue('lifeEvent', returnLifeEventObj ? parsedVal : val)
      setSelectedLifeEvent(returnLifeEventObj ? parsedVal : lifeEvents.find((e) => e.id === val))
    }
  }

  const lifeEventOptions = useMemo(() => {
    return lifeEvents.reduce((acc, e) => {
      if (!e?.name || !e.id) return acc
      const label = e.count ? `${e.name} (${e.count.toLocaleString(i18n.language)})` : e.name
      return [...acc, { label, value: returnLifeEventObj ? JSON.stringify(e) : e.id }]
    }, [])
  }, [lifeEvents, returnLifeEventObj])

  function initialSelected() {
    const primary = []
    const related = []
    if (groupSearchQueryParams?.lifeEventRecordTypes) {
      const split = groupSearchQueryParams.lifeEventRecordTypes.split(',')

      split.forEach((id) => {
        if (primaryRecordTypesList.find((el) => el.id === id)) primary.push(id)
        else related.push(id)
      })
    }
    return { primary, related }
  }

  return (
    <>
      <Select
        placeholder={transl.lifeEventSelectPlaceHolder()}
        label={transl.lifeEvent()}
        name="lifeEventId"
        options={lifeEventOptions}
        value={returnLifeEventObj ? JSON.stringify(selectedLifeEvent) : String(selectedLifeEvent?.id)}
        onChange={selectHandler}
        disabled={disabled}
      />
      {lifeEventList.length > 0 && showCheckBoxes && (
        <ExpandableCheckboxList
          primaryValues={primaryRecordTypesList}
          relatedValues={relatedRecordTypesList}
          values={values}
          setValue={setValue}
          initialSelected={initialSelected()}
        />
      )}
    </>
  )
}

// Currently the use is tailored for the LifeEvent component
// Needs more work if we want it to become generic
function ExpandableCheckboxList({ primaryValues, relatedValues, initialSelected, setValue }) {
  const { getValues } = useZionFormContext()

  const handleInput = useCallback(
    (checkedItems, type) => {
      trackEvent({ link_name: 'life-event-record-type-checkbox' })
      const copy = { ...getValues().lifeEventRecordTypes }
      const newInput = checkedItems?.length > 0 ? checkedItems : []

      if (type === 'primary') copy.primary = newInput
      else copy.related = newInput

      setValue('lifeEventRecordTypes', copy)
    },
    [getValues, setValue]
  )

  const responsiveLabelWidth = css`
    width: 70vw;
    display: inline-block;

    @media screen and (min-width: 600px) {
      width: 210px;
    }
  `

  return (
    <>
      <ExpanderList totalItemCount={primaryValues.length ?? 0} visibleItemCount={4} additionalHeight={60}>
        <CheckboxList
          selectAllLabel={
            <span css={responsiveLabelWidth}>
              <Ellipsis>{transl.lifeEventPrimaryCheckboxListLabel()}</Ellipsis>
            </span>
          }
          onSelectionChange={(e) => handleInput(e, 'primary')}
          initialSelection={initialSelected.primary}
          initialAllSelected={initialSelected?.primary?.length < 1}
        >
          {primaryValues.map((record) => (
            <ListItem
              dense
              key={record.id}
              name={record.id}
              primaryText={<Ellipsis>{record.name}</Ellipsis>}
              endElement={<ListItem.MetaText text={record.count} />}
            />
          ))}
        </CheckboxList>
      </ExpanderList>
      {relatedValues?.length > 0 && (
        <CheckboxList
          selectAllLabel={transl.lifeEventRelatedCheckboxListLabel()}
          onSelectionChange={(e) => handleInput(e, 'related')}
          initialSelection={initialSelected.related}
        >
          {relatedValues.map((record) => (
            <ListItem
              dense
              key={record.id}
              name={record.id}
              primaryText={record.name}
              endElement={<ListItem.MetaText text={record.count} />}
            />
          ))}
        </CheckboxList>
      )}
    </>
  )
}

// Multiple types in the checkboxList and values because
// at some point the parameters types get formatted and we get both types
ExpandableCheckboxList.propTypes = {
  primaryValues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      count: PropTypes.number,
    })
  ),
  relatedValues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      count: PropTypes.number,
    })
  ),
  initialSelected: PropTypes.arrayOf(PropTypes.string),
  setValue: PropTypes.func,
  values: PropTypes.shape({
    lifeEventRecordTypes: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        primary: PropTypes.arrayOf(PropTypes.string),
        related: PropTypes.arrayOf(PropTypes.string),
      }),
    ]),
  }),
  // This is very specific for the lifeEvents because we are controlling two lists
}
